import { useEffect, useId, useState } from 'react';
import { Input } from '@/components/ui/input';
import { formatNumber } from '@/lib/utils';

export interface PaginationJumpProps {
  page: number;
  pageCount: number;
  disabled?: boolean;
  onPageChange: (page: number) => void;
}

/**
 * Direct jump to a page.
 *
 * With ten thousand contacts and fifty per page there are two hundred pages, and
 * «siguiente» is not a way of getting to any of them. It was born inside
 * Contactos, which is the only reason the other eleven lists made you walk.
 */
export function PaginationJump({
  page,
  pageCount,
  disabled = false,
  onPageChange,
}: PaginationJumpProps) {
  const id = useId();
  const [draft, setDraft] = useState(String(page));

  useEffect(() => {
    setDraft(String(page));
  }, [page]);

  const commit = () => {
    const parsed = Number.parseInt(draft, 10);
    if (!Number.isFinite(parsed)) {
      setDraft(String(page));
      return;
    }
    const next = Math.min(Math.max(parsed, 1), pageCount);
    setDraft(String(next));
    if (next !== page) onPageChange(next);
  };

  return (
    <form
      className="flex items-center gap-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        commit();
      }}
    >
      <label htmlFor={id} className="text-xs text-muted-foreground">
        Página
      </label>
      <Input
        id={id}
        inputMode="numeric"
        disabled={disabled}
        value={draft}
        aria-label={`Ir a una página, entre 1 y ${pageCount}`}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        className="h-7 w-14 text-center tabular"
      />
      <span className="text-xs tabular text-muted-foreground">de {formatNumber(pageCount)}</span>
    </form>
  );
}
